import React, { useState, useEffect, useCallback, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPaperPlane, faSpinner, faPlus, faTimes, faEllipsisV,
  faUndo, faClone, faPen, faGripVertical, faTable, faCode
} from '@fortawesome/free-solid-svg-icons';
import httpExchangeService from '../../services/httpExchangeService';
import authProfileService from '../../services/authProfileService';
import repeaterTabService from '../../services/repeaterTabService';
import ProfileSelect from './ProfileSelect';
import ResponseViewer from './ResponseViewer';
import RequestViewer from './RequestViewer';
import HttpSearchBar from './HttpSearchBar';
import { buildSearchRegex } from './HighlightedText';

const GROUP_COLORS = [
  { name: 'Red', value: '#ef4444' },
  { name: 'Orange', value: '#f97316' },
  { name: 'Yellow', value: '#eab308' },
  { name: 'Green', value: '#22c55e' },
  { name: 'Blue', value: '#3b82f6' },
  { name: 'Purple', value: '#a855f7' },
  { name: 'Pink', value: '#ec4899' },
  { name: 'Cyan', value: '#06b6d4' },
];

const RepeaterTab = ({ webTarget, initialExchange, isDarkMode }) => {
  const [tabs, setTabs] = useState([]);
  const [activeTabId, setActiveTabId] = useState(null);
  const [authProfiles, setAuthProfiles] = useState([]);
  const [sending, setSending] = useState(false);
  const [contextMenu, setContextMenu] = useState(null);
  const [renamingTabId, setRenamingTabId] = useState(null);
  const [renameValue, setRenameValue] = useState('');
  const [closedTabs, setClosedTabs] = useState([]);
  const [showClosedMenu, setShowClosedMenu] = useState(false);
  const [dragTabId, setDragTabId] = useState(null);
  const [groupMenu, setGroupMenu] = useState(null);
  const tabBarRef = useRef(null);
  const renameInputRef = useRef(null);
  const saveTimerRef = useRef(null);
  const initialExchangeProcessed = useRef(null);

  // Load auth profiles
  useEffect(() => {
    if (webTarget?.project) {
      authProfileService.getAll(webTarget.project).then(setAuthProfiles).catch(() => {});
    }
  }, [webTarget?.project]);

  // Load tabs from DB
  const loadTabs = useCallback(async () => {
    if (!webTarget?._id) return;
    try {
      const data = await repeaterTabService.getAll({ webTargetId: webTarget._id });
      setTabs(data);
      // If no active tab and we have tabs, select the first one
      if (data.length > 0) {
        setActiveTabId(prev => {
          if (prev && data.find(t => t._id === prev)) return prev;
          return data[0]._id;
        });
      }
    } catch {}
  }, [webTarget?._id]);

  useEffect(() => {
    loadTabs();
  }, [loadTabs]);

  // Handle "Send to Repeater" — create a new tab
  useEffect(() => {
    if (!initialExchange?.request || !webTarget?._id) return;
    // Deduplicate: don't create again for the same exchange
    const exchangeKey = initialExchange._id || `${initialExchange.request.method}:${initialExchange.request.url}`;
    if (initialExchangeProcessed.current === exchangeKey) return;
    initialExchangeProcessed.current = exchangeKey;

    const createTab = async () => {
      try {
        let tabName = 'New Tab';
        try {
          const parsed = new URL(initialExchange.request.url);
          const pathParts = parsed.pathname.split('/').filter(Boolean);
          tabName = pathParts[pathParts.length - 1] || parsed.hostname;
          if (tabName.length > 30) tabName = tabName.slice(0, 30);
        } catch {}

        const newTab = await repeaterTabService.create({
          project: webTarget.project,
          webTarget: webTarget._id,
          name: tabName,
          request: {
            method: initialExchange.request.method || 'GET',
            url: initialExchange.request.url || '',
            headers: (initialExchange.request.headers || []).map(h => `${h.name}: ${h.value}`).join('\n'),
            body: initialExchange.request.body || '',
          },
          sourceExchangeId: initialExchange._id,
        });
        await loadTabs();
        setActiveTabId(newTab._id);
      } catch {}
    };
    createTab();
  }, [initialExchange, webTarget?._id, webTarget?.project, loadTabs]);

  // Load full tab data when switching tabs
  const [activeTabData, setActiveTabData] = useState(null);
  useEffect(() => {
    if (!activeTabId) { setActiveTabData(null); return; }
    repeaterTabService.getById(activeTabId).then(setActiveTabData).catch(() => setActiveTabData(null));
  }, [activeTabId]);

  // Local request state (editable form)
  const [method, setMethod] = useState('GET');
  const [url, setUrl] = useState('');
  const [headers, setHeaders] = useState('');
  const [body, setBody] = useState('');
  // A multi-MB body (e.g. a file upload) freezes a controlled <textarea>. Keep it
  // out of the editable field; store it here and send it as-is.
  const [largeBody, setLargeBody] = useState(null);
  // Form-data editor: key-value pairs with individual encoding (like Burp/Postman).
  // 'raw' = textarea (default), 'form' = key-value table with auto-encoding.
  const [bodyMode, setBodyMode] = useState('raw');
  const [formParams, setFormParams] = useState([]);
  const bodyTextareaRef = useRef(null);

  // Check if Content-Type header indicates form-urlencoded
  const isFormUrlEncoded = /content-type:\s*application\/x-www-form-urlencoded/i.test(headers);

  // Parse raw body text into form params array.
  // Heuristic: if a fragment after splitting by & has no '=', it's not a real
  // parameter — it's a continuation of the previous value that contained a
  // literal '&' (e.g. client_secret=H[(S&K77mB1r → the &K77mB1r part).
  const parseBodyToParams = (raw) => {
    if (!raw || !raw.includes('=')) return [{ key: '', value: '', enabled: true }];
    const parts = raw.split('&').filter(Boolean);
    const params = [];
    for (const part of parts) {
      const eqIdx = part.indexOf('=');
      if (eqIdx < 0 && params.length > 0) {
        // No '=' — append back to previous param's value (with the & that was split off)
        params[params.length - 1].value += '&' + part;
      } else if (eqIdx < 0) {
        params.push({ key: part, value: '', enabled: true });
      } else {
        const key = part.substring(0, eqIdx);
        const rawVal = part.substring(eqIdx + 1);
        let decoded;
        try { decoded = decodeURIComponent(rawVal.replace(/\+/g, ' ')); }
        catch { decoded = rawVal; }
        params.push({ key, value: decoded, enabled: true });
      }
    }
    return params.length ? params : [{ key: '', value: '', enabled: true }];
  };

  // Build encoded body string from form params
  const buildBodyFromParams = (params) => {
    return params
      .filter(p => p.enabled && p.key)
      .map(p => p.key + '=' + encodeURIComponent(p.value).replace(/%20/g, '+'))
      .join('&');
  };

  // Switch between raw and form modes
  const handleBodyModeToggle = (mode) => {
    if (mode === bodyMode) return;
    if (mode === 'form') {
      setFormParams(parseBodyToParams(body));
    } else {
      // Switching back to raw: rebuild body from params (encoded)
      setBody(buildBodyFromParams(formParams));
    }
    setBodyMode(mode);
  };

  // Update a single form param
  const updateFormParam = (idx, field, value) => {
    setFormParams(prev => prev.map((p, i) => i === idx ? { ...p, [field]: value } : p));
  };
  const removeFormParam = (idx) => {
    setFormParams(prev => prev.filter((_, i) => i !== idx));
  };
  const addFormParam = () => {
    setFormParams(prev => [...prev, { key: '', value: '', enabled: true }]);
  };

  // URL-encode selected text in the raw body textarea
  const handleEncodeSelection = () => {
    const ta = bodyTextareaRef.current;
    if (!ta) return;
    const start = ta.selectionStart;
    const end = ta.selectionEnd;
    if (start === end) return; // nothing selected
    const selected = body.substring(start, end);
    const encoded = encodeURIComponent(selected);
    const newBody = body.substring(0, start) + encoded + body.substring(end);
    setBody(newBody);
    // Restore cursor after the encoded text
    setTimeout(() => { ta.selectionStart = ta.selectionEnd = start + encoded.length; ta.focus(); }, 0);
  };

  const [authProfileId, setAuthProfileId] = useState('');
  // When a real profile is selected, refresh cookies + CSRF token from latest traffic
  // (survives portal re-auth). Uncheck to send the headers field exactly as-is.
  const [autoRefreshAuth, setAutoRefreshAuth] = useState(true);
  const [response, setResponse] = useState(null);

  // Shared search across the request/response viewers (like the Traffic tab).
  const [searchRegex, setSearchRegex] = useState(null);
  const [activeMatchIndex, setActiveMatchIndex] = useState(0);
  const [reqMatchCount, setReqMatchCount] = useState(0);
  const [resMatchCount, setResMatchCount] = useState(0);
  const totalMatches = reqMatchCount + resMatchCount;
  const handleSearchChange = (q, opts) => { setSearchRegex(buildSearchRegex(q, opts)); setActiveMatchIndex(0); };
  const handleSearchNext = () => { if (totalMatches) setActiveMatchIndex(i => (i + 1) % totalMatches); };
  const handleSearchPrev = () => { if (totalMatches) setActiveMatchIndex(i => (i - 1 + totalMatches) % totalMatches); };
  const handleSearchClose = () => setSearchRegex(null);
  // The exchange to display in the two viewers: the request AS SENT + the response.
  // On tab restore we don't have the sent request, so reconstruct it from the editor.
  const parseHeaderLines = (str) => (str || '').split('\n').filter(Boolean).map(l => {
    const i = l.indexOf(':');
    return i > 0 ? { name: l.slice(0, i).trim(), value: l.slice(i + 1).trim() } : null;
  }).filter(Boolean);
  const effectiveBody = largeBody != null ? largeBody : (bodyMode === 'form' ? buildBodyFromParams(formParams) : body);
  const resultExchange = response && !response.error
    ? {
      request: response.request || { method, url, headers: parseHeaderLines(headers), body: effectiveBody, queryParams: [] },
      response: response.response || null
    }
    : null;

  // Draggable divider between the request editor and the response area.
  const [composeHeight, setComposeHeight] = useState(260);
  const dividerRef = useRef(null);
  const startDivider = (e) => { dividerRef.current = { startY: e.clientY, startH: composeHeight }; e.preventDefault(); };
  useEffect(() => {
    const move = (e) => {
      if (!dividerRef.current) return;
      const dy = e.clientY - dividerRef.current.startY;
      setComposeHeight(Math.max(140, Math.min(760, dividerRef.current.startH + dy)));
    };
    const up = () => { dividerRef.current = null; };
    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup', up);
    return () => { window.removeEventListener('mousemove', move); window.removeEventListener('mouseup', up); };
  }, []);

  // Sync local state from loaded tab data
  useEffect(() => {
    if (!activeTabData) return;
    let cancelled = false;
    const applyBody = (b) => {
      if ((b || '').length > 100000) { setLargeBody(b); setBody(''); }
      else { setLargeBody(null); setBody(b || ''); }
    };
    const stored = activeTabData.request || {};
    setMethod(stored.method || 'GET');
    setUrl(stored.url || '');
    setHeaders(stored.headers || '');
    setBodyMode('raw');
    setFormParams([]);
    applyBody(stored.body || '');
    // Self-heal tabs created before the full-exchange fetch fix: if the stored body
    // is a tiny placeholder but the headers declare a real body, re-fetch the source
    // captured exchange and use its real request body.
    const storedBody = stored.body || '';
    if (activeTabData.sourceExchangeId && storedBody.length < 60 && /content-length:\s*\d{3,}/i.test(stored.headers || '')) {
      httpExchangeService.getById(activeTabData.sourceExchangeId).then(full => {
        if (cancelled || !full?.request) return;
        setMethod(full.request.method || 'GET');
        setUrl(full.request.url || '');
        setHeaders((full.request.headers || []).map(h => `${h.name}: ${h.value}`).join('\n'));
        applyBody(full.request.body || '');
      }).catch(() => {});
    }
    setAuthProfileId(activeTabData.authProfile || '');
    setResponse(activeTabData.lastResponse ? { response: activeTabData.lastResponse } : null);
    // eslint-disable-next-line no-unused-vars
    const _cleanup = () => { cancelled = true; };
    return _cleanup;
  }, [activeTabData]);

  // Auto-save tab state on change (debounced)
  const saveCurrentTab = useCallback(async () => {
    if (!activeTabId) return;
    try {
      await repeaterTabService.update(activeTabId, {
        request: { method, url, headers, body },
        authProfile: authProfileId || null,
      });
    } catch {}
  }, [activeTabId, method, url, headers, body, authProfileId]);

  useEffect(() => {
    if (!activeTabId) return;
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(saveCurrentTab, 1500);
    return () => { if (saveTimerRef.current) clearTimeout(saveTimerRef.current); };
  }, [method, url, headers, body, authProfileId, saveCurrentTab]);

  // --- Tab actions ---

  const handleNewTab = async () => {
    try {
      const newTab = await repeaterTabService.create({
        project: webTarget.project,
        webTarget: webTarget._id,
        name: `Tab ${tabs.length + 1}`,
      });
      await loadTabs();
      setActiveTabId(newTab._id);
    } catch {}
  };

  const handleCloseTab = async (tabId, e) => {
    e?.stopPropagation();
    try {
      await repeaterTabService.close(tabId);
      // If closing active tab, switch to adjacent
      if (activeTabId === tabId) {
        const idx = tabs.findIndex(t => t._id === tabId);
        const remaining = tabs.filter(t => t._id !== tabId);
        if (remaining.length > 0) {
          setActiveTabId(remaining[Math.min(idx, remaining.length - 1)]._id);
        } else {
          setActiveTabId(null);
          setActiveTabData(null);
        }
      }
      loadTabs();
    } catch {}
  };

  const handleBulkClose = async (mode) => {
    try {
      await repeaterTabService.bulkClose({
        mode,
        referenceId: contextMenu?.tabId,
        webTargetId: webTarget._id,
      });
      setContextMenu(null);
      await loadTabs();
    } catch {}
  };

  const handleDuplicateTab = async (tabId) => {
    try {
      const source = await repeaterTabService.getById(tabId);
      const newTab = await repeaterTabService.create({
        project: webTarget.project,
        webTarget: webTarget._id,
        name: `${source.name} (copy)`,
        request: source.request,
        authProfile: source.authProfile,
      });
      setContextMenu(null);
      await loadTabs();
      setActiveTabId(newTab._id);
    } catch {}
  };

  const handleReopenTab = async (tabId) => {
    try {
      const tab = await repeaterTabService.reopen(tabId);
      setShowClosedMenu(false);
      await loadTabs();
      setActiveTabId(tab._id);
    } catch {}
  };

  const startRename = (tabId) => {
    const tab = tabs.find(t => t._id === tabId);
    setRenamingTabId(tabId);
    setRenameValue(tab?.name || '');
    setContextMenu(null);
    setTimeout(() => renameInputRef.current?.select(), 50);
  };

  const commitRename = async () => {
    if (renamingTabId && renameValue.trim()) {
      try {
        await repeaterTabService.update(renamingTabId, { name: renameValue.trim() });
        loadTabs();
      } catch {}
    }
    setRenamingTabId(null);
  };

  const handleSetGroup = async (tabId, group, color) => {
    try {
      await repeaterTabService.update(tabId, { group, groupColor: color });
      setGroupMenu(null);
      setContextMenu(null);
      loadTabs();
    } catch {}
  };

  // --- Drag & drop reorder ---

  const handleDragStart = (tabId, e) => {
    setDragTabId(tabId);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (tabId, e) => {
    e.preventDefault();
    if (dragTabId && dragTabId !== tabId) {
      const fromIdx = tabs.findIndex(t => t._id === dragTabId);
      const toIdx = tabs.findIndex(t => t._id === tabId);
      if (fromIdx >= 0 && toIdx >= 0 && fromIdx !== toIdx) {
        const reordered = [...tabs];
        const [moved] = reordered.splice(fromIdx, 1);
        reordered.splice(toIdx, 0, moved);
        setTabs(reordered);
      }
    }
  };

  const handleDragEnd = async () => {
    if (dragTabId) {
      const tabIds = tabs.map(t => t._id);
      try {
        await repeaterTabService.reorder(tabIds);
      } catch {}
    }
    setDragTabId(null);
  };

  // --- Context menu ---

  const handleContextMenu = (tabId, e) => {
    e.preventDefault();
    e.stopPropagation();
    setContextMenu({ tabId, x: e.clientX, y: e.clientY });
  };

  // --- Send request ---

  const handleSend = async () => {
    if (!url || sending) return;
    setSending(true);
    try {
      const headerList = headers.split('\n').filter(Boolean).map(line => {
        const idx = line.indexOf(':');
        return idx > 0 ? { name: line.substring(0, idx).trim(), value: line.substring(idx + 1).trim() } : null;
      }).filter(Boolean);

      // Build the body: if in form mode, encode params properly; otherwise use raw body.
      let sendBody;
      if (largeBody != null) {
        sendBody = largeBody;
      } else if (bodyMode === 'form' && ['POST', 'PUT', 'PATCH'].includes(method)) {
        sendBody = buildBodyFromParams(formParams);
      } else {
        sendBody = body;
      }

      const result = await httpExchangeService.replay({
        modifiedRequest: { method, url, headers: headerList, body: sendBody },
        authProfileId: authProfileId || undefined,
        autoRefreshAuth: authProfileId ? autoRefreshAuth : false,
        webTargetId: webTarget._id,
        projectId: webTarget.project
      });

      setResponse(result);

      // Save response to tab
      if (activeTabId) {
        const sendCount = (activeTabData?.sendCount || 0) + 1;
        await repeaterTabService.update(activeTabId, {
          lastResponse: result.response || null,
          lastExchangeId: result._id || null,
          sendCount,
          request: { method, url, headers, body },
        });
        // Update tab list to reflect new status code
        loadTabs();
      }
    } catch (err) {
      setResponse({ error: err.message });
    } finally {
      setSending(false);
    }
  };

  // --- Keyboard shortcuts ---
  useEffect(() => {
    const handler = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
        e.preventDefault();
        handleSend();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [url, method, headers, body, authProfileId, sending]);

  // Group tabs by group name for display
  const groupedTabs = tabs.reduce((acc, tab) => {
    const g = tab.group || '';
    if (!acc[g]) acc[g] = [];
    acc[g].push(tab);
    return acc;
  }, {});

  const tabBg = isDarkMode ? 'bg-gray-800' : 'bg-gray-50';
  const tabBorder = isDarkMode ? 'border-gray-700' : 'border-gray-200';

  return (
    <div className="h-full flex flex-col">
      {/* Tab bar */}
      <div
        ref={tabBarRef}
        className={`flex items-center border-b overflow-x-auto ${tabBorder} ${tabBg}`}
        style={{ scrollbarWidth: 'thin' }}
      >
        <div className="flex items-center flex-nowrap min-w-0">
          {tabs.map(tab => {
            const isActive = tab._id === activeTabId;
            const isDragging = tab._id === dragTabId;
            return (
              <div
                key={tab._id}
                draggable
                onDragStart={(e) => handleDragStart(tab._id, e)}
                onDragOver={(e) => handleDragOver(tab._id, e)}
                onDragEnd={handleDragEnd}
                onClick={() => setActiveTabId(tab._id)}
                onDoubleClick={() => startRename(tab._id)}
                onContextMenu={(e) => handleContextMenu(tab._id, e)}
                className={`flex items-center gap-1 px-2.5 py-1.5 text-xs cursor-pointer whitespace-nowrap border-r select-none transition-colors ${
                  isDragging ? 'opacity-40' : ''
                } ${
                  isActive
                    ? (isDarkMode ? 'bg-gray-900 text-teal-400 border-gray-700' : 'bg-white text-teal-600 border-gray-200')
                    : (isDarkMode ? 'text-gray-400 hover:text-gray-300 hover:bg-gray-750 border-gray-700' : 'text-gray-600 hover:text-gray-800 hover:bg-gray-100 border-gray-200')
                }`}
                style={tab.groupColor ? { borderTop: `2px solid ${tab.groupColor}` } : {}}
              >
                {/* Method badge */}
                <span className={`font-mono text-[10px] font-bold ${
                  { GET: 'text-green-500', POST: 'text-yellow-500', PUT: 'text-blue-500', PATCH: 'text-purple-500', DELETE: 'text-red-500' }[tab.request?.method] || 'text-gray-400'
                }`}>
                  {tab.request?.method || ''}
                </span>

                {/* Tab name */}
                {renamingTabId === tab._id ? (
                  <input
                    ref={renameInputRef}
                    value={renameValue}
                    onChange={e => setRenameValue(e.target.value)}
                    onBlur={commitRename}
                    onKeyDown={e => { if (e.key === 'Enter') commitRename(); if (e.key === 'Escape') setRenamingTabId(null); }}
                    className={`text-xs w-24 px-1 py-0 border rounded outline-none ${
                      isDarkMode ? 'bg-gray-900 border-gray-600 text-gray-200' : 'bg-white border-gray-300 text-gray-800'
                    }`}
                    onClick={e => e.stopPropagation()}
                    autoFocus
                  />
                ) : (
                  <span className="truncate max-w-[120px]">{tab.name}</span>
                )}

                {/* Status indicator */}
                {tab.lastResponse?.statusCode && (
                  <span className={`text-[9px] font-mono font-bold ${
                    tab.lastResponse.statusCode < 300 ? 'text-green-500' : tab.lastResponse.statusCode < 400 ? 'text-blue-400' : tab.lastResponse.statusCode < 500 ? 'text-yellow-500' : 'text-red-500'
                  }`}>
                    {tab.lastResponse.statusCode}
                  </span>
                )}

                {/* Close button */}
                <button
                  onClick={(e) => handleCloseTab(tab._id, e)}
                  className={`ml-0.5 p-0.5 rounded-sm hover:bg-red-500/20 hover:text-red-400 text-[8px] ${
                    isActive ? '' : 'opacity-0 group-hover:opacity-100'
                  }`}
                  style={{ opacity: isActive ? 1 : undefined }}
                >
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              </div>
            );
          })}
        </div>

        {/* New tab button */}
        <button
          onClick={handleNewTab}
          className={`flex-shrink-0 px-2.5 py-1.5 text-xs ${isDarkMode ? 'text-gray-500 hover:text-gray-300 hover:bg-gray-700' : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'}`}
          title="New tab (Ctrl+T)"
        >
          <FontAwesomeIcon icon={faPlus} />
        </button>

        {/* Reopen closed tabs */}
        <div className="relative flex-shrink-0">
          <button
            onClick={() => {
              repeaterTabService.getClosed({ webTargetId: webTarget._id }).then(setClosedTabs).catch(() => {});
              setShowClosedMenu(p => !p);
            }}
            className={`px-2 py-1.5 text-[10px] ${isDarkMode ? 'text-gray-500 hover:text-gray-300' : 'text-gray-400 hover:text-gray-600'}`}
            title="Recently closed tabs"
          >
            <FontAwesomeIcon icon={faUndo} />
          </button>
          {showClosedMenu && (
            <>
              <div className="fixed inset-0 z-40" onClick={() => setShowClosedMenu(false)} />
              <div className={`absolute right-0 top-full mt-1 z-50 rounded-lg border shadow-lg w-64 py-1 max-h-64 overflow-y-auto ${
                isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
              }`}>
                {closedTabs.length === 0 ? (
                  <div className={`px-3 py-2 text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>No recently closed tabs</div>
                ) : (
                  closedTabs.map(ct => (
                    <button
                      key={ct._id}
                      onClick={() => handleReopenTab(ct._id)}
                      className={`w-full text-left px-3 py-1.5 text-xs flex items-center gap-2 ${
                        isDarkMode ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      <span className="font-mono text-[10px] font-bold text-gray-500">{ct.request?.method}</span>
                      <span className="truncate">{ct.name}</span>
                    </button>
                  ))
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Active tab content */}
      {activeTabId ? (
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Request editor (resizable via the divider below) */}
          <div className={`p-3 overflow-auto ${tabBg}`} style={{ height: composeHeight, flexShrink: 0 }}>
            <div className="flex items-center gap-2 mb-2">
              <select
                value={method}
                onChange={e => setMethod(e.target.value)}
                className={`text-xs font-bold rounded border px-2 py-1.5 ${isDarkMode ? 'bg-gray-900 border-gray-600 text-gray-300' : 'bg-white border-gray-300 text-gray-700'}`}
              >
                {['GET', 'POST', 'PUT', 'PATCH', 'DELETE', 'OPTIONS', 'HEAD'].map(m => (
                  <option key={m} value={m}>{m}</option>
                ))}
              </select>
              <input
                type="text"
                value={url}
                onChange={e => setUrl(e.target.value)}
                placeholder="https://example.com/api/endpoint"
                className={`flex-1 text-xs rounded border px-3 py-1.5 ${isDarkMode ? 'bg-gray-900 border-gray-600 text-gray-300 placeholder-gray-500' : 'bg-white border-gray-300 text-gray-700 placeholder-gray-400'}`}
                onKeyDown={e => { if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') handleSend(); }}
              />
              <ProfileSelect
                profiles={authProfiles}
                value={authProfileId}
                onChange={setAuthProfileId}
                darkMode={isDarkMode}
              />
              {authProfileId && (
                <label
                  title="Refresh cookies + ServiceNow CSRF token (X-UserToken) from the latest captured traffic before sending. Survives portal re-auth. Uncheck to send the Headers field exactly as-is."
                  className={`flex items-center gap-1 text-[10px] whitespace-nowrap cursor-pointer ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  <input type="checkbox" checked={autoRefreshAuth} onChange={e => setAutoRefreshAuth(e.target.checked)} />
                  auto-refresh auth
                </label>
              )}
              <button
                onClick={handleSend}
                disabled={sending || !url}
                className="px-4 py-1.5 bg-teal-600 text-white rounded text-xs font-medium hover:bg-teal-700 disabled:opacity-50 flex items-center gap-1.5"
              >
                {sending ? <FontAwesomeIcon icon={faSpinner} spin /> : <FontAwesomeIcon icon={faPaperPlane} />}
                Send
              </button>
            </div>

            <div className="flex gap-3">
              <div className="flex-1">
                <div className={`text-[10px] font-medium mb-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>Headers (one per line, Name: Value)</div>
                <textarea
                  value={headers}
                  onChange={e => setHeaders(e.target.value)}
                  rows={9}
                  className={`w-full text-xs font-mono rounded border px-2 py-1 resize-y ${isDarkMode ? 'bg-gray-900 border-gray-600 text-gray-300' : 'bg-white border-gray-300 text-gray-700'}`}
                  placeholder="Content-Type: application/json&#10;Accept: */*"
                />
              </div>
              {['POST', 'PUT', 'PATCH'].includes(method) && (
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`text-[10px] font-medium ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>Body</span>
                    {/* Mode toggle: Raw vs Form Data */}
                    <div className={`flex rounded overflow-hidden border text-[9px] ${isDarkMode ? 'border-gray-600' : 'border-gray-300'}`}>
                      <button
                        type="button"
                        onClick={() => handleBodyModeToggle('raw')}
                        className={`px-1.5 py-0.5 flex items-center gap-1 ${
                          bodyMode === 'raw'
                            ? 'bg-teal-600 text-white'
                            : isDarkMode ? 'bg-gray-800 text-gray-400 hover:bg-gray-700' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                        }`}
                        title="Edit body as raw text"
                      >
                        <FontAwesomeIcon icon={faCode} className="text-[8px]" /> Raw
                      </button>
                      <button
                        type="button"
                        onClick={() => handleBodyModeToggle('form')}
                        className={`px-1.5 py-0.5 flex items-center gap-1 ${
                          bodyMode === 'form'
                            ? 'bg-teal-600 text-white'
                            : isDarkMode ? 'bg-gray-800 text-gray-400 hover:bg-gray-700' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                        }`}
                        title="Edit as key-value pairs (auto-encodes special characters)"
                      >
                        <FontAwesomeIcon icon={faTable} className="text-[8px]" /> Form
                      </button>
                    </div>
                    {/* URL-encode selection button (raw mode only) */}
                    {bodyMode === 'raw' && (
                      <button
                        type="button"
                        onClick={handleEncodeSelection}
                        className={`text-[9px] px-1.5 py-0.5 rounded border ${isDarkMode ? 'border-gray-600 text-gray-400 hover:bg-gray-700 hover:text-gray-200' : 'border-gray-300 text-gray-500 hover:bg-gray-100 hover:text-gray-700'}`}
                        title="Select text in the body, then click to URL-encode it"
                      >
                        %encode
                      </button>
                    )}
                  </div>
                  {largeBody != null ? (
                    <div className={`text-xs rounded border px-2 py-2 ${isDarkMode ? 'bg-gray-900 border-gray-600 text-gray-400' : 'bg-gray-50 border-gray-300 text-gray-500'}`} style={{ minHeight: 200 }}>
                      Large body ({(largeBody.length / 1048576).toFixed(1)} MB, e.g. a file upload) — not shown here to keep the editor responsive. It will be sent <strong>as captured</strong>.
                      <div className="mt-2 flex flex-wrap gap-3">
                        <button
                          type="button"
                          onClick={() => { setBody(largeBody.slice(0, 100000)); setLargeBody(null); }}
                          className={`underline ${isDarkMode ? 'text-teal-400' : 'text-teal-600'}`}
                        >Edit first 100 KB</button>
                        <button
                          type="button"
                          onClick={() => { setBody(largeBody); setLargeBody(null); }}
                          className={`underline ${isDarkMode ? 'text-amber-400' : 'text-amber-600'}`}
                          title="Loads the entire body into the editor — the browser may lag for a moment."
                        >Edit full body (may lag)</button>
                      </div>
                    </div>
                  ) : bodyMode === 'form' ? (
                    /* Form Data key-value editor */
                    <div className={`rounded border overflow-auto ${isDarkMode ? 'bg-gray-900 border-gray-600' : 'bg-white border-gray-300'}`} style={{ maxHeight: 220 }}>
                      <table className="w-full text-xs font-mono">
                        <thead>
                          <tr className={isDarkMode ? 'bg-gray-800' : 'bg-gray-50'}>
                            <th className={`w-6 px-1 py-1 text-center text-[9px] font-medium ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}></th>
                            <th className={`px-2 py-1 text-left text-[9px] font-medium ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>Name</th>
                            <th className={`px-2 py-1 text-left text-[9px] font-medium ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>Value</th>
                            <th className={`w-6 px-1 py-1 text-[9px] font-medium ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}></th>
                          </tr>
                        </thead>
                        <tbody>
                          {formParams.map((param, idx) => (
                            <tr key={idx} className={`border-t ${isDarkMode ? 'border-gray-700' : 'border-gray-200'} ${!param.enabled ? 'opacity-40' : ''}`}>
                              <td className="px-1 py-0.5 text-center">
                                <input
                                  type="checkbox"
                                  checked={param.enabled}
                                  onChange={e => updateFormParam(idx, 'enabled', e.target.checked)}
                                  className="accent-teal-500"
                                  title="Include this parameter"
                                />
                              </td>
                              <td className="px-1 py-0.5">
                                <input
                                  type="text"
                                  value={param.key}
                                  onChange={e => updateFormParam(idx, 'key', e.target.value)}
                                  className={`w-full bg-transparent border-none outline-none px-1 py-0.5 text-xs font-mono ${isDarkMode ? 'text-gray-300 placeholder-gray-600' : 'text-gray-700 placeholder-gray-400'}`}
                                  placeholder="name"
                                />
                              </td>
                              <td className="px-1 py-0.5">
                                <input
                                  type="text"
                                  value={param.value}
                                  onChange={e => updateFormParam(idx, 'value', e.target.value)}
                                  className={`w-full bg-transparent border-none outline-none px-1 py-0.5 text-xs font-mono ${isDarkMode ? 'text-teal-300 placeholder-gray-600' : 'text-teal-700 placeholder-gray-400'}`}
                                  placeholder="value (special chars auto-encoded on send)"
                                />
                              </td>
                              <td className="px-1 py-0.5 text-center">
                                <button
                                  type="button"
                                  onClick={() => removeFormParam(idx)}
                                  className={`text-[9px] px-0.5 rounded hover:bg-red-500/20 hover:text-red-400 ${isDarkMode ? 'text-gray-600' : 'text-gray-400'}`}
                                  title="Remove parameter"
                                >
                                  <FontAwesomeIcon icon={faTimes} />
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      <button
                        type="button"
                        onClick={addFormParam}
                        className={`w-full text-left px-3 py-1 text-[10px] border-t ${isDarkMode ? 'border-gray-700 text-gray-500 hover:bg-gray-800 hover:text-gray-300' : 'border-gray-200 text-gray-400 hover:bg-gray-50 hover:text-gray-600'}`}
                      >
                        <FontAwesomeIcon icon={faPlus} className="mr-1" /> Add parameter
                      </button>
                    </div>
                  ) : (
                    <textarea
                      ref={bodyTextareaRef}
                      value={body}
                      onChange={e => setBody(e.target.value)}
                      rows={9}
                      className={`w-full text-xs font-mono rounded border px-2 py-1 resize-y ${isDarkMode ? 'bg-gray-900 border-gray-600 text-gray-300' : 'bg-white border-gray-300 text-gray-700'}`}
                      placeholder='{"key": "value"}'
                    />
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Draggable divider */}
          <div
            onMouseDown={startDivider}
            className={`h-1.5 cursor-ns-resize flex-shrink-0 hover:bg-teal-500 transition-colors ${isDarkMode ? 'bg-gray-700' : 'bg-gray-300'}`}
            title="Drag to resize"
          />

          {/* Response area — the request AS SENT (injected cookies + refreshed
              X-UserToken) and the response, side-by-side with shared search, like Traffic. */}
          <div className="flex-1 min-h-0 flex flex-col">
            {response?.error ? (
              <div className="p-4 text-red-500 text-sm">{response.error}</div>
            ) : resultExchange ? (
              <>
                <div className={`px-3 py-1.5 text-xs font-semibold border-b flex items-center justify-between ${isDarkMode ? 'border-gray-700 text-teal-400' : 'border-gray-200 text-teal-600'}`}>
                  <span>Sent request / Response</span>
                  <span className="text-[10px] font-normal text-gray-500">Status: {response.response?.statusCode ?? '—'}</span>
                </div>
                <HttpSearchBar
                  isDarkMode={isDarkMode}
                  visible={true}
                  onSearchChange={handleSearchChange}
                  totalMatches={totalMatches}
                  activeMatchIndex={activeMatchIndex}
                  onNext={handleSearchNext}
                  onPrev={handleSearchPrev}
                  onClose={handleSearchClose}
                />
                <div className="flex flex-1 min-h-0">
                  <div className={`flex-1 overflow-auto ${isDarkMode ? 'border-r border-gray-700' : 'border-r border-gray-200'}`}>
                    <RequestViewer exchange={resultExchange} isDarkMode={isDarkMode} searchRegex={searchRegex} activeMatchIndex={activeMatchIndex} matchOffset={0} onMatchCount={setReqMatchCount} />
                  </div>
                  <div className="flex-1 overflow-auto">
                    <ResponseViewer exchange={resultExchange} isDarkMode={isDarkMode} searchRegex={searchRegex} activeMatchIndex={activeMatchIndex} matchOffset={reqMatchCount} onMatchCount={setResMatchCount} />
                  </div>
                </div>
              </>
            ) : (
              <div className={`flex items-center justify-center h-full text-sm ${isDarkMode ? 'text-gray-600' : 'text-gray-400'}`}>
                Enter a URL and click Send (Ctrl+Enter) to make a request.
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className={`flex-1 flex items-center justify-center text-sm ${isDarkMode ? 'text-gray-600' : 'text-gray-400'}`}>
          <div className="text-center">
            <p>No tabs open. Click + to create a new tab or send a request to Repeater.</p>
          </div>
        </div>
      )}

      {/* Context menu */}
      {contextMenu && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setContextMenu(null)} />
          <div
            className={`fixed z-50 rounded-lg border shadow-lg py-1 w-52 ${
              isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
            }`}
            style={{ left: contextMenu.x, top: contextMenu.y }}
          >
            <button
              onClick={() => startRename(contextMenu.tabId)}
              className={`w-full text-left px-3 py-1.5 text-xs flex items-center gap-2 ${isDarkMode ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-700 hover:bg-gray-50'}`}
            >
              <FontAwesomeIcon icon={faPen} className="w-3 text-center" /> Rename
            </button>
            <button
              onClick={() => handleDuplicateTab(contextMenu.tabId)}
              className={`w-full text-left px-3 py-1.5 text-xs flex items-center gap-2 ${isDarkMode ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-700 hover:bg-gray-50'}`}
            >
              <FontAwesomeIcon icon={faClone} className="w-3 text-center" /> Duplicate
            </button>

            <div className={`my-1 border-t ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`} />

            {/* Group submenu */}
            <div className="relative">
              <button
                onClick={() => setGroupMenu(groupMenu ? null : contextMenu.tabId)}
                className={`w-full text-left px-3 py-1.5 text-xs flex items-center justify-between ${isDarkMode ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-700 hover:bg-gray-50'}`}
              >
                <span><FontAwesomeIcon icon={faGripVertical} className="w-3 text-center mr-2" />Tab group</span>
                <span className="text-[10px] opacity-50">▶</span>
              </button>
              {groupMenu && (
                <div className={`absolute left-full top-0 ml-1 rounded-lg border shadow-lg py-1 w-40 ${
                  isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
                }`}>
                  <button
                    onClick={() => handleSetGroup(contextMenu.tabId, '', '')}
                    className={`w-full text-left px-3 py-1 text-xs ${isDarkMode ? 'text-gray-400 hover:bg-gray-700' : 'text-gray-500 hover:bg-gray-50'}`}
                  >
                    No group
                  </button>
                  {GROUP_COLORS.map(gc => (
                    <button
                      key={gc.value}
                      onClick={() => handleSetGroup(contextMenu.tabId, gc.name, gc.value)}
                      className={`w-full text-left px-3 py-1 text-xs flex items-center gap-2 ${isDarkMode ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-700 hover:bg-gray-50'}`}
                    >
                      <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: gc.value }} />
                      {gc.name}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className={`my-1 border-t ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`} />

            <button
              onClick={() => handleCloseTab(contextMenu.tabId)}
              className={`w-full text-left px-3 py-1.5 text-xs flex items-center gap-2 ${isDarkMode ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-700 hover:bg-gray-50'}`}
            >
              <FontAwesomeIcon icon={faTimes} className="w-3 text-center" /> Close
            </button>
            <button
              onClick={() => handleBulkClose('others')}
              className={`w-full text-left px-3 py-1.5 text-xs flex items-center gap-2 ${isDarkMode ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-700 hover:bg-gray-50'}`}
            >
              <span className="w-3" /> Close other tabs
            </button>
            <button
              onClick={() => handleBulkClose('left')}
              className={`w-full text-left px-3 py-1.5 text-xs flex items-center gap-2 ${isDarkMode ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-700 hover:bg-gray-50'}`}
            >
              <span className="w-3" /> Close tabs to the left
            </button>
            <button
              onClick={() => handleBulkClose('right')}
              className={`w-full text-left px-3 py-1.5 text-xs flex items-center gap-2 ${isDarkMode ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-700 hover:bg-gray-50'}`}
            >
              <span className="w-3" /> Close tabs to the right
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default RepeaterTab;
